import {
  type HighlightMode,
  type HighlightRange,
  type HighlightsRenderer,
} from '@databases/highlights';
import {
  HIGHLIGHT_COLUMN_CLASS,
  HIGHLIGHT_SELECTION_CLASS,
} from '@databases/highlights/constants';

import { type SelectionMode, type SelectionRange } from '../../../machine/state/types';
import {
  ROW_HIGHLIGHTS_LAYER_Z_INDEX,
  BEHIND_STICKY_COLUMNS_HIGHLIGHTS_LAYER_Z_INDEX,
} from '../constants';

import { isSingleCellSelection } from './is-single-cell-selection';

/**
 * Acts as the strict bridge between the application's Selection State Machine
 * and the framework-agnostic Highlights rendering engine.
 *
 * Why do we need this?
 * 1. Translation: Maps the complex, nested `SelectionRange` state objects into the
 * flat `HighlightRange` coordinate format the dumb renderer expects.
 * 2. Business Rules: Enforces visual logic that the renderer shouldn't know about:
 * - Idle states immediately clear the layer.
 * - Single-cell selections are switched to 'none' (DOM node is kept in memory
 * but hidden, because the blue Focus box handles single-cell UI).
 * 3. Resiliency: Uses an Upsert pattern (`layer.add()`) so if a DOM node is
 * accidentally deleted, the next mouse drag will instantly recreate it.
 *
 * @param layer - The target renderer layer (e.g., the selection background layer)
 * @param mode - The current selection mode from the state machine ('cell', 'row', 'column', 'idle')
 * @param range - The active state machine selection range to sync to the DOM
 */
export function syncRangeToHighlightsLayer(
  layer: HighlightsRenderer,
  mode: SelectionMode,
  range: SelectionRange
) {
  if (mode === 'idle') {
    layer.remove(range.id);

    return;
  }

  const mappedRange: HighlightRange = {
    id: range.id,
    anchor: { row: range.anchor.row.index, col: range.anchor.col.index },
    lead: { row: range.lead.row.index, col: range.lead.col.index },
  };

  // If it's a single cell, we pass 'none'.
  // The renderer returns null bounds for 'none', and safely sets `display: none`
  // on the DOM node while keeping it fully tracked in memory
  // because when we have just a single cell, we hide the highlight so that only
  // the focus cell is visible.
  let finalMode = mode as HighlightMode;

  if (isSingleCellSelection(range, mode)) {
    finalMode = 'none';
  }

  // Always use `add()`. Our renderer's `add()` method is an Upsert.
  // This guarantees that if the node was missing, it recreates it before updating.
  const className =
    finalMode === 'column'
      ? `${HIGHLIGHT_SELECTION_CLASS} ${HIGHLIGHT_COLUMN_CLASS}`
      : HIGHLIGHT_SELECTION_CLASS;

  layer.add({
    range: mappedRange,
    mode: finalMode,
    zIndex:
      finalMode === 'row' || finalMode === 'container'
        ? ROW_HIGHLIGHTS_LAYER_Z_INDEX
        : BEHIND_STICKY_COLUMNS_HIGHLIGHTS_LAYER_Z_INDEX,
    className,
  });
}
